// component/Geospatial/displayPoints.js
//
// Works out where each Geospatial Overview record is drawn on the map.
//
//  1. A record's own GPS is trusted when it falls inside (near) its state.
//  2. GPS that lands far outside the record's state (e.g. an "Abia" abattoir
//     whose coordinates sit in Abuja), or no GPS at all, is drawn near that
//     state's capital instead and flagged as approximate — the detail panel
//     still shows the recorded GPS so it can be corrected at source.
//  3. Records sharing the same spot are fanned out on a small ring (tens of
//     metres) so every marker and its legend icon stays visible and
//     clickable, rather than stacking into one point.
//
// The record's own `lat`/`lng` are never changed; the drawn position goes on
// `mapLat`/`mapLng` and the reason on `locationAccuracy`.

import { normalizeGpsPoint } from '../../services/gpsCoordinates';
import { STATE_CAPITAL_COORDS } from '../../Pages/public/data/nigeriaStates';

// Farther than this from its state capital and a point can't be in that state
// (Nigeria's largest states reach roughly 250 km from their capitals).
const MAX_STATE_RADIUS_KM = 250;

const STATE_ALIASES = {
  federalcapitalterritory: 'fct',
  abuja: 'fct',
  fctabuja: 'fct',
  abujafct: 'fct',
};

function stateKey(name) {
  const key = String(name ?? '').toLowerCase().replace(/[^a-z]/g, '').replace(/state$/, '');
  return STATE_ALIASES[key] ?? key;
}

const CAPITALS = new Map(
  Object.entries(STATE_CAPITAL_COORDS).map(([name, [lat, lng]]) => [stateKey(name), { lat, lng }])
);

function distanceKm(a, b) {
  const rad = Math.PI / 180;
  const dLat = (b.lat - a.lat) * rad;
  const dLng = (b.lng - a.lng) * rad;
  const h = Math.sin(dLat / 2) ** 2
    + Math.cos(a.lat * rad) * Math.cos(b.lat * rad) * Math.sin(dLng / 2) ** 2;
  return 2 * 6371 * Math.asin(Math.sqrt(h));
}

// Stable 0..1 values from a record id, so positions don't jump between renders
function hashUnit(text, salt) {
  let h = 2166136261 ^ salt;
  for (const ch of String(text)) {
    h ^= ch.charCodeAt(0);
    h = Math.imul(h, 16777619);
  }
  return ((h >>> 0) % 10000) / 10000;
}

function offsetMetres(point, metres, angle) {
  const dLat = (metres * Math.cos(angle)) / 111320;
  const dLng = (metres * Math.sin(angle)) / (111320 * Math.cos((point.lat * Math.PI) / 180));
  return { lat: point.lat + dLat, lng: point.lng + dLng };
}

function resolvePoint(record) {
  const gps = normalizeGpsPoint({ lat: record.lat, lng: record.lng });
  const capital = CAPITALS.get(stateKey(record.state));

  if (gps && (!capital || distanceKm(gps, capital) <= MAX_STATE_RADIUS_KM)) {
    return { point: gps, accuracy: 'gps' };
  }
  if (capital) {
    // Spread approximate records 3–12 km around the capital so several in one
    // state don't all sit on the same pin
    const key = record.id ?? record.name;
    const spread = offsetMetres(capital, 3000 + 9000 * hashUnit(key, 1), 2 * Math.PI * hashUnit(key, 2));
    return { point: spread, accuracy: gps ? 'outside-state' : 'no-gps' };
  }
  return null;
}

export function withDisplayPoints(records = []) {
  const resolved = records.map((record) => ({ record, ...resolvePoint(record) }));

  // Fan out records that land on the same spot (to ~1 m)
  const groups = new Map();
  resolved.forEach((entry) => {
    if (!entry.point) return;
    const key = `${entry.point.lat.toFixed(5)},${entry.point.lng.toFixed(5)}`;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(entry);
  });
  groups.forEach((group) => {
    if (group.length < 2) return;
    group.sort((a, b) => String(a.record.id).localeCompare(String(b.record.id)));
    const centre = group[0].point;
    const radius = 30 + 10 * group.length;
    group.forEach((entry, i) => {
      entry.point = offsetMetres(centre, radius, (2 * Math.PI * i) / group.length);
    });
  });

  return resolved.map(({ record, point, accuracy }) => ({
    ...record,
    mapLat: point?.lat ?? null,
    mapLng: point?.lng ?? null,
    locationAccuracy: accuracy ?? null,
  }));
}

// Where a record is drawn (falls back to its own GPS for records that never
// went through withDisplayPoints)
export function mapPointOf(record) {
  if (record?.mapLat != null && record?.mapLng != null) return { lat: record.mapLat, lng: record.mapLng };
  return normalizeGpsPoint(record);
}
